import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useRouter } from '@/routing/navigation'
import { Loader2, CheckCircle2, AlertTriangle } from '@digital-twins/design-system/icons'
import { authApi } from '@/lib/api'
import { isPasswordValid } from '@/lib/utils/password-validation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { BackToLogin } from '@/components/auth/BackToLogin'
import { PasswordRulesChecklist } from '@/components/auth/PasswordRulesChecklist'
import { PasswordInput } from '@/components/auth/PasswordInput'

export default function ResetPasswordPage() {
  const { t } = useTranslation(['auth', 'common'])
  const searchParams = useSearchParams()
  const router = useRouter()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const token = searchParams.get('token') || ''

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => router.push('/login'), 2000)
      return () => clearTimeout(timer)
    }
  }, [success, router])

  if (!token) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertTriangle className="size-12 text-destructive" />
          <Heading level={2}>{t('reset.invalidTitle')}</Heading>
          <Text as="p" color="muted" className="text-base">
            {t('reset.invalidBody')}
          </Text>
        </div>
        <BackToLogin />
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError(t('reset.mismatch'))
      return
    }

    setLoading(true)

    try {
      await authApi.resetPassword(token, password)
      setSuccess(true)
    } catch (err: any) {
      setError(err.response?.data?.message || t('reset.errors.resetFailed'))
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-4 text-center">
          <CheckCircle2 className="size-12 text-primary" />
          <Heading level={2}>{t('reset.successTitle')}</Heading>
          <Text as="p" color="muted" className="text-base">
            {t('reset.successBody')}
          </Text>
        </div>
      </div>
    )
  }

  const passwordsMatch = confirmPassword.length === 0 || password === confirmPassword
  const canSubmit = isPasswordValid(password) && password === confirmPassword && !loading

  return (
    <div className="flex w-full flex-col gap-6">
      <Heading level={1} className="text-center">
        {t('reset.title')}
      </Heading>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="space-y-2">
          <Label htmlFor="password">{t('reset.newPassword')}</Label>
          <PasswordInput
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            autoComplete="new-password"
          />
          <PasswordRulesChecklist password={password} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm-password">{t('reset.confirmPassword')}</Label>
          <PasswordInput
            id="confirm-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
            required
            autoComplete="new-password"
          />
          {!passwordsMatch && (
            <p className="text-xs text-destructive">{t('reset.mismatch')}</p>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <Button type="submit" variant="primary" disabled={!canSubmit} className="w-full">
          {loading ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              {t('reset.submitting')}
            </>
          ) : (
            t('reset.submit')
          )}
        </Button>
      </form>

      <BackToLogin />
    </div>
  )
}
